import { gql, useMutation, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import {
  OrganizationUserAccessTypes,
  OrganizationUserEntityRelationTypes,
} from '@interfaces/organizationUser';
import type { User } from '@interfaces/user';
import { EntityTypes } from '@interfaces/user';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { ClientSelectField } from '@ovComponents/2-component/clientSelectField/clientSelect';
import { Dialog } from '@ovComponents/2-component/dialog/dialog';
import { Form } from '@ovComponents/2-component/form/form';
import { MenuItem } from '@ovComponents/2-component/menuItem/menuItem';
import {
  type Segment,
  SegmentedControl,
  type SegmentValue,
} from '@ovComponents/2-component/segmentedControl/segmentedControl';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { ConfirmationModal } from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { SwitchField } from '@ovComponents/3-pattern/switchField/switchField';
import {
  ADD_ENTITY_TO_ORG_USER,
  REMOVE_ENTITY_FROM_ORG_USER,
} from '@ovComponents/4-module/widgets/accessToEntity/accessToEntity.queries';
import { UPDATE_AFFILIATION } from '@ovComponents/4-module/workflowCompletion/subSteps/account/updateAffiliationMutation';
import { CREATE_WORKFLOW_COMPLETION } from '@ovComponents/4-module/workflowCompletion/workflowCompletion.queries';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { entityName } from '@utils/common';
import { Box, Button, Grid, Typography } from 'hephaestus/web';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

enum GrantAccessActions {
  CREATE = 'createNewUser',
  INVITE = 'inviteUser',
}

export const FETCH_ROLES = gql`
query fetchRoles($input: FetchRolesInput!) {
  fetchRoles(input: $input) {
    roles {
      id
      translatedName { ${TRANSLATED_STRING_FIELDS} }
      organization {
        translatedName { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
}
`;

export const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
        affiliateOnly
      }
    }
  }
`;

export const CREATE_ORGANIZATION_USER = gql`
  mutation createOrganizationUser($input: CreateOrganizationUserInput!) {
    createOrganizationUser(input: $input) {
      organizationUser {
        id
      }
    }
  }
`;

const TRANSITION_USER = gql`
  mutation transitionUser($input: TransitionUserInput!) {
    transitionUser(input: $input) {
      user {
        id
        state
      }
    }
  }
`;

export const ConvertAffiliate = ({
  entity,
  openConvertAffiliate,
  setOpenConvertAffiliate,
  currentAffiliate,
  accountData,
  options,
  refetch,
}: {
  entity?: User;
  openConvertAffiliate: boolean;
  setOpenConvertAffiliate: any;
  currentAffiliate: any;
  accountData?: any;
  options?: any;
  refetch?: () => void;
}) => {
  const { t } = useTranslation(['components']);
  const canWriteOrganizationUsers = usePermission('write:organization_users');
  const canWriteInviteOrganizationUsers = usePermission(
    'write:invite_organization_users',
  );
  const { activeOrganization } = useContext(UserContext);
  const [grantAccessAction, setGrantAccessAction] =
    useState<GrantAccessActions>(GrantAccessActions.CREATE);
  const emptyUser = {
    firstName: currentAffiliate?.user?.firstName ?? '',
    lastName: currentAffiliate?.user?.lastName ?? '',
    email: currentAffiliate?.user?.primaryEmail ?? '',
    organizationId: activeOrganization.id ?? '',
    roleId: '',
    autoInviteUser: true,
  };
  const [removeEntityFromOrganizationUser] = useMutation(
    REMOVE_ENTITY_FROM_ORG_USER,
  );
  const [updateUser] = useMutation(UPDATE_USER);
  const [createWorkflowCompletion] = useMutation(CREATE_WORKFLOW_COMPLETION);
  const [updateAffiliations] = useMutation(UPDATE_AFFILIATION);
  const [transitionUser] = useMutation(TRANSITION_USER);
  const [addEntityToOrganizationUser] = useMutation(ADD_ENTITY_TO_ORG_USER, {
    onCompleted: refetch,
  });
  const [organizationUser, setOrganizationUser] = useState(emptyUser);
  const [newOrgUserRelation, setNewOrgUserRelation] = useState(
    OrganizationUserEntityRelationTypes.OWNER,
  );
  const [affiliateConversionConfirmation, setAffiliateConversionConfirmation] =
    useState(false);
  const [emailError, setEmailError] = useState(false);
  const [entityUser, setEntityUser] = useState<User | undefined>();
  const [loading, setLoading] = useState(false);
  const canCreateAndInvite =
    canWriteOrganizationUsers || canWriteInviteOrganizationUsers;
  const { loading: rolesLoading, data } = useQuery(FETCH_ROLES, {
    variables: {
      input: {
        filter: {
          organizationId: organizationUser.organizationId || undefined,
        },
        pagination: { perPage: 50 },
      },
    },
  });

  const handleClose = () => {
    setOpenConvertAffiliate(false);
    setOrganizationUser(emptyUser);
    setNewOrgUserRelation(OrganizationUserEntityRelationTypes.OWNER);
    setEmailError(false);
  };

  useEffect(() => {
    if (!organizationUser.organizationId) {
      setOrganizationUser((prev) => ({ ...prev, roleId: '' }));
    }
  }, [organizationUser.organizationId]);

  const [createOrganizationUser] = useMutation(CREATE_ORGANIZATION_USER, {
    variables: {
      input: {
        ...organizationUser,
        accessType: OrganizationUserAccessTypes.ENTITY,
      },
    },
    onError: (error) => {
      if (
        ['duplicate key error', organizationUser.email].every((elem) =>
          error.message.includes(elem),
        )
      )
        setEmailError(true);
    },
  });

  const segments: Segment[] = [
    {
      value: GrantAccessActions.CREATE,
      label: t('components:accessToEntity.addButton.toggle.create'),
    },
    {
      value: GrantAccessActions.INVITE,
      label: t('components:accessToEntity.addButton.toggle.invite'),
    },
  ];

  const handleSubmit = async () => {
    if (
      !canCreateAndInvite ||
      grantAccessAction === GrantAccessActions.INVITE
    ) {
      setAffiliateConversionConfirmation(true);
    } else {
      setLoading(true);
      await updateUser({
        variables: {
          input: {
            userId: currentAffiliate?.user?.id,
            affiliateOnly: false,
            convertedFromAffiliateInvitation: true,
          },
        },
      });

      const dataOrgUser = await createOrganizationUser();
      const organizationUserId =
        dataOrgUser?.data?.createOrganizationUser?.organizationUser?.id;
      if (organizationUserId) {
        // remove existing accessibleBy users
        const existingAceessiblenUsers =
          currentAffiliate?.user?.accessibleBy.filter(
            (accessibleBy: any) =>
              accessibleBy.organizationUser.id !== organizationUserId,
          );
        if (existingAceessiblenUsers) {
          await Promise.all(
            existingAceessiblenUsers.map(async (accessibleBy: any) => {
              await removeEntityFromOrganizationUser({
                variables: {
                  input: {
                    entityId: currentAffiliate?.user?.id,
                    organizationUserId: accessibleBy.organizationUser.id,
                  },
                },
              });
            }),
          );
        }
        // add new accessibleBy user
        const entityAddingResponse = await addEntityToOrganizationUser({
          variables: {
            input: {
              entityId: currentAffiliate?.user?.id,
              organizationUserId,
              relation: newOrgUserRelation,
              readOnly: false,
            },
          },
        });

        if (accountData.user.type !== EntityTypes.INDIVIDUAL) {
          await addEntityToOrganizationUser({
            variables: {
              input: {
                entityId: accountData.user.id,
                organizationUserId,
                relation: OrganizationUserEntityRelationTypes.OTHER,
                readOnly: false,
              },
            },
          });
        }

        const entityOrgId =
          entityAddingResponse?.data?.addEntityToOrganizationUser
            ?.organizationUser?.id;
        if (
          entityOrgId &&
          options.convertedAffiiateWorkflow &&
          options?.convertedAffiiateWorkflow !== 'default'
        ) {
          await createWorkflowCompletion({
            variables: {
              input: {
                objectId: currentAffiliate?.user?.id,
                objectType: 'USER',
                workflowId: options.convertedAffiiateWorkflow,
                organizationId: organizationUser.organizationId,
              },
            },
          });
        }
      }
      if (refetch) refetch();
      handleClose();
      setLoading(false);
    }
  };

  const isSubmissionDisabled =
    !canCreateAndInvite || grantAccessAction === GrantAccessActions.INVITE
      ? !entityUser
      : !(!Object.values(organizationUser).includes('') && newOrgUserRelation);

  return (
    <>
      <Dialog
        sx={{
          '.MuiDialog-container': {
            '.MuiPaper-root': {
              padding: '20px',
            },
          },
        }}
        open={openConvertAffiliate}
        onClose={handleClose}
        fullWidth
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'end',
          }}
        >
          <Box
            onClick={handleClose}
            sx={{
              display: 'flex',
              cursor: 'pointer',
            }}
          >
            <Icon icon={CloseRoundedIcon} size='medium' />
          </Box>
        </Box>
        <Form onSubmit={handleSubmit}>
          <Grid
            container
            sx={{
              gap: canCreateAndInvite ? 2 : 4,
            }}
          >
            <Grid size={12}>
              <Typography variant='headingMedium' sx={{ mt: 1 }}>
                {t('components:accessToEntity.addButton.formTitle', {
                  entityName: entityName(entity),
                })}
              </Typography>
            </Grid>
            {canCreateAndInvite && (
              <SegmentedControl
                sx={{ mb: 4 }}
                value={grantAccessAction}
                segments={segments}
                exclusive={true}
                fullWidth
                enforceActive
                onChange={(value: SegmentValue) => {
                  if (grantAccessAction === GrantAccessActions.CREATE) {
                    setOrganizationUser(emptyUser);
                    setNewOrgUserRelation(
                      OrganizationUserEntityRelationTypes.OWNER,
                    );
                    setEmailError(false);
                  }
                  setGrantAccessAction(value as GrantAccessActions);
                }}
              />
            )}
            {canCreateAndInvite &&
              grantAccessAction === GrantAccessActions.CREATE && (
                <>
                  <Grid size={12}>
                    <TextField
                      label={t(
                        'components:accessToEntity.addButton.createTab.firstName',
                      )}
                      fullWidth
                      value={organizationUser.firstName}
                      onChange={(e: any) =>
                        setOrganizationUser({
                          ...organizationUser,
                          firstName: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label={t(
                        'components:accessToEntity.addButton.createTab.lastName',
                      )}
                      fullWidth
                      value={organizationUser.lastName}
                      onChange={(e: any) =>
                        setOrganizationUser({
                          ...organizationUser,
                          lastName: e.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      label={t(
                        'components:accessToEntity.addButton.createTab.email',
                      )}
                      fullWidth
                      value={organizationUser.email}
                      onChange={(e: any) => {
                        setOrganizationUser({
                          ...organizationUser,
                          email: e.target.value.trim(),
                        });
                        setEmailError(false);
                      }}
                      error={emailError}
                      errorText={t(
                        'components:accessToEntity.addButton.createTab.emailError',
                      )}
                    />
                  </Grid>
                  <Grid size={12}>
                    <SelectField
                      value={organizationUser.roleId}
                      label={t(
                        'components:accessToEntity.addButton.createTab.role',
                      )}
                      onChange={(e: any) =>
                        setOrganizationUser({
                          ...organizationUser,
                          roleId: e.target.value,
                        })
                      }
                      fullWidth
                      disabled={organizationUser.organizationId === ''}
                    >
                      {rolesLoading ? (
                        <MenuItem>...</MenuItem>
                      ) : (
                        data.fetchRoles.roles.map((x: any) => (
                          <MenuItem key={x.id} value={x.id}>
                            {translateBackend(x.translatedName)} -{' '}
                            {translateBackend(x.organization.translatedName)}
                          </MenuItem>
                        ))
                      )}
                    </SelectField>
                  </Grid>
                  <Grid size={12}>
                    <SwitchField
                      checked={organizationUser?.autoInviteUser ?? true}
                      onChange={(checked) =>
                        setOrganizationUser({
                          ...organizationUser,
                          autoInviteUser: checked,
                        })
                      }
                      label={t(
                        'components:accessToEntity.addButton.createTab.autoInviteUser',
                      )}
                    />
                  </Grid>
                </>
              )}
            {(!canCreateAndInvite ||
              grantAccessAction === GrantAccessActions.INVITE) && (
              <Grid size={12}>
                <ClientSelectField
                  fullWidth
                  isJointOrAuthorizedIndividual
                  autoFocus
                  label={t('components:clientSelect.selectUser')}
                  user={undefined}
                  setUser={(user: User | undefined) => {
                    setEntityUser(user);
                  }}
                />
              </Grid>
            )}
            <Grid
              size={12}
              sx={{
                mt: 3,
                justifyContent: 'end',
                display: 'flex',
              }}
            >
              <Button
                type='submit'
                color='primary'
                disabled={isSubmissionDisabled || loading}
              >
                {t('components:affiliateConversion.inviteUser')}
              </Button>
            </Grid>
          </Grid>
        </Form>
      </Dialog>
      <ConfirmationModal
        onCancel={() => setAffiliateConversionConfirmation(false)}
        onConfirm={async () => {
          if (accountData) {
            const affiliatestionToUpdate: any[] = [];
            accountData?.affiliations.forEach((aff: any) => {
              if (aff?.user?.id === currentAffiliate?.user?.id) {
                affiliatestionToUpdate.push({
                  allocation: aff.allocation ?? 0,
                  id: aff?.id,
                  relation: aff?.relation,
                  signatureRequired: aff?.signatureRequired,
                  type: aff?.type,
                  userId: entityUser?.id,
                });
              } else {
                affiliatestionToUpdate.push({
                  allocation: aff.allocation ?? 0,
                  id: aff?.id,
                  relation: aff?.relation,
                  signatureRequired: aff?.signatureRequired,
                  type: aff?.type,
                  userId: aff?.user?.id,
                });
              }
            });
            await updateAffiliations({
              variables: {
                input: {
                  accountId: accountData?.accountId,
                  affiliations: affiliatestionToUpdate,
                },
              },
            });
          }
          await transitionUser({
            variables: {
              input: {
                userId: currentAffiliate?.user?.id,
                transition: 'deactivate',
              },
            },
          });
          if (refetch) refetch();
          setAffiliateConversionConfirmation(false);
          handleClose();
        }}
        open={affiliateConversionConfirmation}
        title={t('components:affiliateConversion.confirmation.title')}
        bodyText={t('components:affiliateConversion.confirmation.subTitle', {
          entityUserEmail: entityUser?.primaryEmail,
        })}
      />
    </>
  );
};
